import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiLink, FiPlus, FiTrash2, FiSettings, FiGrid, FiCopy, FiArrowRight } = FiIcons;

export default function LinkGenerator({ onSendToStickers }) {
  const [baseUrl, setBaseUrl] = useState('https://heroes.by/delivery');
  
  const [staticParams, setStaticParams] = useState([
    { id: 1, key: 'cinema', value: 'minsk-city' },
    { id: 2, key: 'hall', value: '4-heroes' }
  ]);

  const [rows, setRows] = useState([
    { id: 1, rowNum: 1, start: 1, end: 10 }
  ]);

  const [quickRows, setQuickRows] = useState(5);
  const [quickSeats, setQuickSeats] = useState(15);
  
  const [generatedLinks, setGeneratedLinks] = useState('');
  const [isCopied, setIsCopied] = useState(false);

  // Автоматическая регенерация при изменении параметров
  useEffect(() => {
    generateLinks();
  }, [baseUrl, staticParams, rows]);

  const addStaticParam = () => {
    setStaticParams([...staticParams, { id: Date.now(), key: '', value: '' }]);
  };

  const removeStaticParam = (id) => {
    setStaticParams(staticParams.filter(p => p.id !== id));
  };

  const updateStaticParam = (id, field, value) => {
    setStaticParams(staticParams.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const applyQuickSetup = () => {
    const newRows = [];
    for (let i = 1; i <= quickRows; i++) {
      newRows.push({ id: Date.now() + i, rowNum: i, start: 1, end: quickSeats });
    }
    setRows(newRows);
  };

  const addRow = () => {
    const nextRowNum = rows.length > 0 ? Math.max(...rows.map(r => r.rowNum)) + 1 : 1;
    setRows([...rows, { id: Date.now(), rowNum: nextRowNum, start: 1, end: 10 }]);
  };

  const removeRow = (id) => {
    setRows(rows.filter(r => r.id !== id));
  };

  const updateRow = (id, field, value) => {
    setRows(rows.map(r => r.id === id ? { ...r, [field]: parseInt(value) || 0 } : r));
  };

  const generateLinks = () => {
    if (!baseUrl) {
      setGeneratedLinks('');
      return;
    }

    try {
      const result = [];
      rows.forEach(r => {
        for (let s = r.start; s <= r.end; s++) {
          const url = new URL(baseUrl);
          
          staticParams.forEach(p => {
            if (p.key && p.value) {
              url.searchParams.append(p.key, p.value);
            }
          });
          
          url.searchParams.append('row', r.rowNum);
          url.searchParams.append('seat', s);
          
          result.push(url.toString());
        }
      });
      setGeneratedLinks(result.join('\n'));
    } catch (e) {
      setGeneratedLinks('Ошибка сборки URL. Проверьте базовую ссылку.');
    }
  };

  const handleCopy = () => {
    if (!generatedLinks) return;
    navigator.clipboard.writeText(generatedLinks).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    });
  };

  const linksCount = generatedLinks ? generatedLinks.split('\n').filter(l => l.startsWith('http')).length : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
      {/* Левая колонка: Настройки */}
      <div className="lg:col-span-7 xl:col-span-8 flex flex-col gap-6">
        
        {/* Базовые настройки */}
        <div className="bg-panelBg rounded-xl border border-gray-800 overflow-hidden">
          <div className="bg-panelCard p-4 border-b border-gray-800 flex items-center gap-2">
            <SafeIcon icon={FiSettings} className="text-accent" />
            <h2 className="font-bold text-sm tracking-widest uppercase">Базовые параметры</h2>
          </div>
          <div className="p-5 flex flex-col gap-5">
            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">Базовая ссылка</label>
              <input 
                type="text" 
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                className="w-full p-2.5 bg-inputBg border border-gray-700 rounded-lg focus:ring-1 focus:ring-accent text-sm text-gray-200 outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-textMuted uppercase tracking-wider mb-2">
                Статичные параметры (кинотеатр, зал и т.д.)
              </label>
              <div className="space-y-2">
                {staticParams.map(param => (
                  <div key={param.id} className="flex gap-2 items-center">
                    <input 
                      type="text" 
                      placeholder="Ключ (напр. cinema)" 
                      value={param.key}
                      onChange={(e) => updateStaticParam(param.id, 'key', e.target.value)}
                      className="flex-1 p-2 bg-inputBg border border-gray-700 rounded-lg text-sm text-gray-200 outline-none"
                    />
                    <span className="text-gray-500">=</span>
                    <input 
                      type="text" 
                      placeholder="Значение (напр. minsk-city)" 
                      value={param.value}
                      onChange={(e) => updateStaticParam(param.id, 'value', e.target.value)}
                      className="flex-1 p-2 bg-inputBg border border-gray-700 rounded-lg text-sm text-gray-200 outline-none"
                    />
                    <button 
                      onClick={() => removeStaticParam(param.id)}
                      className="p-2 text-gray-500 hover:text-red-400 transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                ))}
              </div>
              <button 
                onClick={addStaticParam}
                className="mt-3 flex items-center gap-1 text-xs font-bold text-accent hover:text-accentHover uppercase tracking-wider"
              >
                <SafeIcon icon={FiPlus} /> Добавить параметр
              </button>
            </div>
          </div>
        </div>

        {/* Конфигуратор мест */}
        <div className="bg-panelBg rounded-xl border border-gray-800 overflow-hidden">
          <div className="bg-panelCard p-4 border-b border-gray-800 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiGrid} className="text-accent" />
              <h2 className="font-bold text-sm tracking-widest uppercase">Сетка мест</h2>
            </div>
            
            {/* Быстрая генерация рядов */}
            <div className="flex items-center gap-2 text-xs bg-inputBg p-1.5 rounded-lg border border-gray-700">
              <span className="text-gray-400 pl-2">Быстро:</span>
              <input 
                type="number" min="1" max="50" 
                value={quickRows} onChange={(e) => setQuickRows(e.target.value)}
                className="w-12 p-1 bg-panelBg border border-gray-600 rounded text-center"
                title="Количество рядов"
              />
              <span className="text-gray-500">x</span>
              <input 
                type="number" min="1" max="100" 
                value={quickSeats} onChange={(e) => setQuickSeats(e.target.value)}
                className="w-12 p-1 bg-panelBg border border-gray-600 rounded text-center"
                title="Мест в ряду"
              />
              <button 
                onClick={applyQuickSetup}
                className="bg-accent text-black px-3 py-1 rounded font-bold hover:bg-accentHover"
              >
                Создать
              </button>
            </div>
          </div>

          <div className="p-5">
            <div className="grid grid-cols-12 gap-2 mb-2 px-2 text-xs font-semibold text-textMuted uppercase tracking-wider">
              <div className="col-span-3">Номер ряда</div>
              <div className="col-span-4">Место (ОТ)</div>
              <div className="col-span-4">Место (ДО)</div>
              <div className="col-span-1 text-center">Удал.</div>
            </div>
            
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {rows.map(row => (
                <div key={row.id} className="grid grid-cols-12 gap-2 items-center bg-inputBg p-2 rounded-lg border border-gray-800">
                  <div className="col-span-3">
                    <input 
                      type="number" value={row.rowNum}
                      onChange={(e) => updateRow(row.id, 'rowNum', e.target.value)}
                      className="w-full p-2 bg-panelBg border border-gray-700 rounded-md text-sm text-center"
                    />
                  </div>
                  <div className="col-span-4">
                    <input 
                      type="number" value={row.start}
                      onChange={(e) => updateRow(row.id, 'start', e.target.value)}
                      className="w-full p-2 bg-panelBg border border-gray-700 rounded-md text-sm text-center"
                    />
                  </div>
                  <div className="col-span-4">
                    <input 
                      type="number" value={row.end}
                      onChange={(e) => updateRow(row.id, 'end', e.target.value)}
                      className="w-full p-2 bg-panelBg border border-gray-700 rounded-md text-sm text-center"
                    />
                  </div>
                  <div className="col-span-1 flex justify-center">
                    <button 
                      onClick={() => removeRow(row.id)}
                      className="text-gray-500 hover:text-red-400"
                    >
                      <SafeIcon icon={FiTrash2} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button 
              onClick={addRow}
              className="mt-4 flex items-center gap-1 text-xs font-bold text-accent hover:text-accentHover uppercase tracking-wider"
            >
              <SafeIcon icon={FiPlus} /> Добавить ряд вручную
            </button>
          </div>
        </div>
      </div>

      {/* Правая колонка: Результат */}
      <div className="lg:col-span-5 xl:col-span-4 h-full">
        <div className="bg-panelBg rounded-xl border border-gray-800 h-full flex flex-col overflow-hidden sticky top-8">
          <div className="bg-panelCard p-4 border-b border-gray-800 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiLink} className="text-accent" />
              <h2 className="font-bold text-sm tracking-widest uppercase">Результат</h2>
            </div>
            <span className="text-xs bg-[#2a2a2a] px-2 py-1 rounded text-accent font-mono font-bold">
              {linksCount} ссылок
            </span>
          </div>
          
          <div className="flex-1 p-5 flex flex-col gap-4">
            <textarea 
              readOnly
              value={generatedLinks}
              className="w-full flex-1 min-h-[300px] p-3 bg-inputBg border border-gray-700 rounded-lg text-sm font-mono text-gray-300 outline-none resize-none custom-scrollbar"
              placeholder="Здесь появятся сгенерированные ссылки..."
            />
            
            <div className="flex flex-col gap-3">
              <button 
                onClick={handleCopy}
                disabled={!generatedLinks}
                className={`flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-bold tracking-wide transition-all uppercase text-sm border ${
                  isCopied 
                    ? 'bg-green-500 text-black border-green-500' 
                    : 'bg-panelCard border-gray-700 hover:border-accent hover:text-accent text-white disabled:opacity-50 disabled:hover:border-gray-700 disabled:hover:text-white'
                }`}
              >
                <SafeIcon icon={isCopied ? FiIcons.FiCheck : FiCopy} />
                {isCopied ? 'Скопировано!' : 'Копировать в буфер'}
              </button>

              <button 
                onClick={() => onSendToStickers(generatedLinks)}
                disabled={!generatedLinks}
                className="flex items-center justify-center gap-2 bg-accent hover:bg-accentHover disabled:bg-gray-700 disabled:text-gray-500 text-black py-3 px-4 rounded-xl font-bold tracking-wide transition-all uppercase text-sm"
              >
                <SafeIcon icon={FiArrowRight} />
                Передать в Наклейки
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}